import { useState } from 'react';
import { RefreshCw, Users, Clock, UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PinValidationSection } from '@/components/guard/PinValidationSection';
import { QrScannerSection } from '@/components/guard/QrScannerSection';
import { CrewOnSiteSection } from '@/components/guard/CrewOnSiteSection';
import { VisitorCard } from '@/components/guard/VisitorCard';
import { useGateQueue } from '@/hooks/useGateQueue';
import { useGuardContext } from '@/hooks/useGuardContext';
import { useTranslation } from '@/hooks/useTranslation';

/**
 * Gate Queue — Guard home screen.
 * Displays currently checked-in visitors and upcoming visitors.
 * Polls every 30s via useGateQueue hook.
 */
export function GateQueuePage() {
  const { t } = useTranslation();
  const { gateId, isInvitesEnabled } = useGuardContext();
  const {
    checkedIn,
    upcoming,
    walkInsOnSite,
    isLoading,
    error,
    refetch,
    checkOut,
    isCheckingOut,
    setAside,
    isSettingAside,
  } = useGateQueue(gateId);

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl text-[var(--text-primary)]">{t('guard.queue.title')}</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={refetch}
          aria-label={t('common.retry')}
          className="min-w-[44px] min-h-[44px]"
        >
          <RefreshCw className="size-4" />
        </Button>
      </div>

      {/* Summary counts */}
      <div className="grid grid-cols-3 gap-4">
        <SummaryCard
          icon={<Users className="size-5 text-[var(--status-green-text)]" />}
          count={checkedIn.length}
          label={t('guard.queue.checkedInCount')}
          isLoading={isLoading}
        />
        <SummaryCard
          icon={<Clock className="size-5 text-[var(--status-blue-text)]" />}
          count={upcoming.length}
          label={t('guard.queue.upcomingCount')}
          isLoading={isLoading}
        />
        <SummaryCard
          icon={<UserPlus className="size-5 text-[var(--status-purple-text)]" />}
          count={walkInsOnSite}
          label={t('guard.queue.pendingWalkIns')}
          isLoading={isLoading}
        />
      </div>

      {/* Error state */}
      {error != null && !isLoading && (
        <div className="rounded-[var(--radius-lg)] border border-[var(--border-error)] bg-[var(--status-red-tint)] p-4">
          <p className="text-sm text-[var(--text-error)]">
            {(error as { message?: string })?.message ?? t('common.error')}
          </p>
          <Button variant="outline" size="sm" onClick={refetch} className="mt-2 min-h-[44px]">
            {t('common.retry')}
          </Button>
        </div>
      )}

      {/* QR Scanner + PIN Validation — tabs, only when GUARD_WEB_INVITES is active */}
      {isInvitesEnabled && <ValidationTabs />}

      {/* Currently Checked In section */}
      <section aria-labelledby="checked-in-heading">
        <h2
          id="checked-in-heading"
          className="text-base font-medium text-[var(--text-primary)] mb-3"
        >
          {t('guard.queue.checkedInSection')}
        </h2>

        {isLoading ? (
          <LoadingSkeleton count={3} />
        ) : checkedIn.length === 0 ? (
          <EmptyState message={t('guard.queue.emptyCheckedIn')} />
        ) : (
          <div className="space-y-3">
            {checkedIn.map((visitor) => (
              <VisitorCard
                key={visitor.visitId}
                variant="checked-in"
                visitId={visitor.visitId}
                visitorName={visitor.visitorName}
                hostUnit={visitor.hostUnit}
                checkedInAt={visitor.checkedInAt}
                invitationType={visitor.invitationType}
                {...(visitor.vehiclePlate ? { vehiclePlate: visitor.vehiclePlate } : {})}
                onCheckOut={checkOut}
                isCheckingOut={isCheckingOut}
                isFlagged={visitor.isFlagged === true}
                flagReason={visitor.flagReason ?? null}
                onSetAside={setAside}
                isSettingAside={isSettingAside}
              />
            ))}
          </div>
        )}
      </section>

      {/* Contractor crews still on site — headcount checkout */}
      <CrewOnSiteSection />

      {/* Upcoming Visitors section — only when GUARD_WEB_INVITES is active */}
      {isInvitesEnabled && (
        <section aria-labelledby="upcoming-heading">
          <h2
            id="upcoming-heading"
            className="text-base font-medium text-[var(--text-primary)] mb-3"
          >
            {t('guard.queue.upcomingSection')}
          </h2>

          {isLoading ? (
            <LoadingSkeleton count={2} />
          ) : upcoming.length === 0 ? (
            <EmptyState message={t('guard.queue.emptyUpcoming')} />
          ) : (
            <div className="space-y-3">
              {upcoming.map((visitor) => (
                <VisitorCard
                  key={visitor.invitationId}
                  variant="upcoming"
                  invitationId={visitor.invitationId}
                  visitorName={visitor.visitorName}
                  hostUnit={visitor.hostUnit}
                  hostName={visitor.hostName}
                  invitationType={visitor.invitationType}
                  validFrom={visitor.validFrom}
                  validUntil={visitor.validUntil}
                  {...(visitor.vehiclePlate ? { vehiclePlate: visitor.vehiclePlate } : {})}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// ─── Validation Tabs (QR Scanner + PIN) ──────────────────────────────────────

function ValidationTabs() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'qr' | 'pin'>('qr');

  return (
    <div className="space-y-3">
      <div className="flex rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-1">
        <button
          type="button"
          onClick={() => setActiveTab('qr')}
          className={[
            'flex-1 rounded-[var(--radius-sm)] py-2 text-xs font-medium transition-colors min-h-[36px]',
            activeTab === 'qr'
              ? 'bg-[var(--text-accent)] text-white'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          ].join(' ')}
        >
          {'📷 '}
          {t('guard.qr.title')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('pin')}
          className={[
            'flex-1 rounded-[var(--radius-sm)] py-2 text-xs font-medium transition-colors min-h-[36px]',
            activeTab === 'pin'
              ? 'bg-[var(--text-accent)] text-white'
              : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]',
          ].join(' ')}
        >
          {'# '}
          {t('guard.pin.title')}
        </button>
      </div>
      {activeTab === 'qr' ? <QrScannerSection /> : <PinValidationSection />}
    </div>
  );
}

// ─── Summary Card ────────────────────────────────────────────────────────────

function SummaryCard({
  icon,
  count,
  label,
  isLoading,
}: {
  icon: React.ReactNode;
  count: number;
  label: string;
  isLoading: boolean;
}) {
  return (
    <div className="flex items-center gap-3 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4">
      {icon}
      <div className="flex flex-col">
        {isLoading ? (
          <Skeleton className="h-6 w-8" />
        ) : (
          <span className="text-lg font-semibold text-[var(--text-primary)] tabular-nums">
            {count}
          </span>
        )}
        <span className="text-xs text-[var(--text-secondary)]">{label}</span>
      </div>
    </div>
  );
}

// ─── Empty State ─────────────────────────────────────────────────────────────

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-8 text-center">
      <p className="text-sm text-[var(--text-secondary)]">{message}</p>
    </div>
  );
}

// ─── Loading Skeleton ────────────────────────────────────────────────────────

function LoadingSkeleton({ count }: { count: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 rounded-[var(--radius-lg)] border border-[var(--border-default)] bg-[var(--bg-surface)] p-4"
        >
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-48" />
          </div>
          <Skeleton className="h-9 w-20" />
        </div>
      ))}
    </div>
  );
}
